import { useCallback, useId, useRef, useState } from 'react';
import { UploadCloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ProgressBar } from '@/components/ui/progress';
import {
  DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES,
  DOCUMENT_SIGN_MAX_SOURCE_FILES,
  DOCUMENT_SIGN_SOURCE_MIME_TYPE,
} from '@/lib/api';
import { useUploadDocument, DocumentUploadError } from '../hooks';

interface DocumentUploaderProps {
  envelopeId: string;
  /** Number of source files already attached. Used to enforce the per-envelope cap. */
  currentCount: number;
  /** Per-envelope cap on source files; defaults to the platform constant. */
  maxFiles?: number;
  disabled?: boolean;
}

interface UploadFeedback {
  fileName: string;
  loaded: number;
  total: number;
}

export function DocumentUploader({
  envelopeId,
  currentCount,
  maxFiles = DOCUMENT_SIGN_MAX_SOURCE_FILES,
  disabled = false,
}: DocumentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const upload = useUploadDocument();
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState<UploadFeedback | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remaining = Math.max(0, maxFiles - currentCount);
  const isAtCap = remaining === 0;
  const isInteractable = !disabled && !isAtCap && !upload.isPending;

  const uploadFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);

      const usable = list.slice(0, remaining);
      if (list.length > usable.length) {
        setError(
          `Only ${usable.length} of ${list.length} files can be added (${maxFiles} max per envelope).`,
        );
      }

      for (const file of usable) {
        if (file.type !== DOCUMENT_SIGN_SOURCE_MIME_TYPE) {
          setError(`"${file.name}" is not a PDF and was skipped.`);
          continue;
        }
        if (file.size > DOCUMENT_SIGN_MAX_FILE_SIZE_BYTES) {
          setError(`"${file.name}" exceeds the 25 MB limit and was skipped.`);
          continue;
        }
        setProgress({ fileName: file.name, loaded: 0, total: file.size });
        try {
          await upload.mutateAsync({
            envelopeId,
            file,
            onProgress: (loaded, total) => setProgress({ fileName: file.name, loaded, total }),
          });
        } catch (err) {
          if (err instanceof DocumentUploadError) {
            setError(`"${file.name}": ${err.message}`);
          } else {
            setError(`"${file.name}": ${err instanceof Error ? err.message : 'Upload failed'}`);
          }
        } finally {
          setProgress(null);
        }
      }
    },
    [envelopeId, maxFiles, remaining, upload],
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (!isInteractable) return;
    if (e.dataTransfer.files?.length) {
      void uploadFiles(e.dataTransfer.files);
    }
  };

  return (
    <div className="space-y-3">
      <div
        role="button"
        tabIndex={isInteractable ? 0 : -1}
        onClick={() => isInteractable && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (isInteractable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          if (isInteractable) setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (isInteractable) setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-8 text-center transition-colors',
          isInteractable
            ? 'cursor-pointer border-muted-foreground/30 hover:border-primary/50'
            : 'cursor-not-allowed border-muted-foreground/20 opacity-60',
          dragActive && 'border-primary bg-primary/5',
        )}
        aria-disabled={!isInteractable}
        aria-describedby={`${inputId}-help`}
      >
        <UploadCloud className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <div className="text-sm font-medium">
          {isAtCap ? `Maximum ${maxFiles} files reached` : 'Drop PDFs here or click to browse'}
        </div>
        <div id={`${inputId}-help`} className="text-xs text-muted-foreground">
          PDF only · Up to 25 MB · {remaining} of {maxFiles} slots remaining
        </div>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept="application/pdf"
          multiple
          className="sr-only"
          disabled={!isInteractable}
          onChange={(e) => {
            if (e.target.files?.length) {
              void uploadFiles(e.target.files);
              // Reset so the same file can be re-selected if needed.
              e.target.value = '';
            }
          }}
        />
      </div>

      {progress && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="truncate">Uploading {progress.fileName}</span>
            <span>{Math.round((progress.loaded / Math.max(progress.total, 1)) * 100)}%</span>
          </div>
          <ProgressBar value={(progress.loaded / Math.max(progress.total, 1)) * 100} />
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      {!isAtCap && !progress && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!isInteractable}
          onClick={() => inputRef.current?.click()}
        >
          Browse files
        </Button>
      )}
    </div>
  );
}
